import { Component, Input, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import * as groupBy from 'lodash/groupBy';
import { Router } from '@angular/router';

import { Airport } from '../models/airport.model';
import { LandingSurface, LandingSurfaceType } from '../models/landing-surface.model';
import { BusinessFuel } from '../../business/models/business-fuel.model';
import { getSubTags } from '../../util/tag-utils';
import { AirportComment, CommentType } from '../models/airport-comment.model';
import { SearchQuery } from '../../search/models/search-query.model';
import { AuthService } from '../../auth/auth/auth.service';
import { IAuthEvent } from '../../auth/models/auth-event.model';
import { UserService } from '../../shared/user/user.service';
import { UserInfo } from '../../shared/models/user-info.model';
import { State } from '../../shared/state-view/state.model';
import { Source } from '../../shared/models/source.model';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { MatDialog, MatDialogConfig, MatSnackBar } from '@angular/material';
import { EditGaTransientComponent } from '../edit-ga-transient/edit-ga-transient.component';
import { AirportService } from '../airport.service';
import {BusinessSummary} from '../models/business-summary.model';
import * as moment from 'moment-timezone';
// import * as SunCalc from 'suncalc';

/**
 * Radius (in nautical miles) to use for nearby airport search.
 */
export const NEARBY_RADIUS = 25;


@Component({
  selector: 'app-airport-overview',
  templateUrl: './airport-overview.component.html',
  styleUrls: ['./airport-overview.component.scss']
})
export class AirportOverviewComponent implements OnInit {
  @Input() airport: Airport;
  @Input() isIsland?: boolean;
  @Input() isSubscribedToIslandGuide?: boolean;
  authenticated = false;
  state = State.Empty;
  private _userInfo: UserInfo;
  Source = Source;
  editModeSet = false;

  constructor(private _decimalPipe: DecimalPipe,
              private _auth: AuthService,
              private _userService: UserService,
              private _toggleService: EditToggleService,
              public _snackBar: MatSnackBar,
              private _editGATransientDialog: MatDialog,
              private _airportService: AirportService,
              private _router: Router) {
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
  }

  ngOnInit() {
    // Update state based on latest event in authStream, and subscribe to any future
    // changes in authentication state.
    this._onAuthEvent(this._auth.authStream.getValue());
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));

    // Update state based on latest available userInfo, and subscribe to any future
    // changes to userInfo.
    this._userInfo = this._userService.userInfo.getValue();
    this._userService.userInfo.subscribe(u => {
      // Update userInfo
      this._userInfo = u;
      // Set state to loaded
      this.state = State.Content;
    });
  }

  private _onAuthEvent(e: IAuthEvent) {
    this.authenticated = this._auth.authenticated;
    if (this.authenticated && !this._auth.endUser) {
      // userInfo is not available when not authenticated as an end user, so set favorite state to loaded now.
      this.state = State.Content;
    }
  }

  get elevationMethod(): string {
    if (this.airport.elevationMethod !== null) {
      return Airport.formatElevationMethod(this.airport.elevationMethod);
    }
    return '';
  }

  get fbos(): BusinessSummary[] {
    return this.airport.businesses.filter(a => a.isFbo)
      .filter(fbo => !fbo.hideBusiness)
      .filter(fbo => !fbo.notApproved)
      .sort( (bs1, bs2) =>
      bs1.name < bs2.name ? -1 : bs1.name > bs2.name ? 1 : 0
    );
  }

  get towerFacilityType(): string {
    if (this.airport.towerFacilityType !== null) {
      return Airport.formatTowerFacilityType(this.airport.towerFacilityType);
    }
    return '';
  }

  /**
   * Returns list of attendance remarks that apply to this airport.
   */
  get attendanceRemarks(): Array<AirportComment> {
    return this.airport.getComments(CommentType.Attendance);
  }

  /**
   * Returns list of tower remarks that apply to this airport.
   * @returns {Array<AirportComment>}
   */
  get towerRemarks(): Array<AirportComment> {
    return this.airport.getComments(CommentType.Tower);
  }

  /**
   * Returns the airport runways, sorted by runway length in descending order
   */
  get runways(): Array<LandingSurface> {
    if (!this.airport || !this.airport.landingSurfaces) {
      return null;
    }
    return this.airport.landingSurfaces.sort((rwy1: LandingSurface, rwy2: LandingSurface) => {
      if (!rwy2.length) {
        return -1;
      }
      return rwy2.length - rwy1.length;
    });
  }

  /**
   * Returns the text to display for the "Tower" field if the airport is towered.
   * @returns {string}
   */
  get towerText(): string {
    return [
      Airport.formatTowerFacilityType(this.airport.towerFacilityType),
      this.airport.towerHrsPerDay ? this.airport.towerHrsPerDay + ' hrs/day' : null,
      this.airport.towerRegularityOfOps
    ].filter(v => v).join(', ');
  }

  /**
   * Returns fees that are applicable to this airport.
   */
  get fees(): string {
    let result: string[] = [];
    let hasOther = false;
    const feesWanted: string[] = ['Ramp', 'Hangar', 'Tie Down', 'Landing', 'Overnight', 'Handling', 'Parking'];
    if (this.airport.businesses != null) {
      for (const busFee of this.airport.businessFees) {
        for (const currentFee of feesWanted) {
          if (busFee === currentFee) {
            result.push(busFee);
          } else {
            hasOther = true;
          }
        }
      }
    }
    //  Filters array by uniqueness
    result = result.filter((value, i, self) => self.indexOf(value) === i).sort();
    if (hasOther) {
      result.push('Other');
    }
    return result.join(', ');
  }

  /**
   * Returns an array of all fuel types available at any FBO at this airport.
   */
  get allFuel(): Array<BusinessFuel> {
    return [].concat.apply([], this.airport.businesses.map(b => b.fuel));
  }

  /**
   * Returns the minimum fuel prices for each combination of fuel and service type.
   */
  get minimumFuelPrices(): Array<BusinessFuel> {
    const groups = groupBy(this.allFuel, (f: BusinessFuel) => {
      return f.fuelType + '|' + f.service;
    });
    const retVal = [];
    /* return Object.values(groups)
      .map(fuelPrices => fuelPrices.sort((f1: BusinessFuel, f2: BusinessFuel) => f1.price - f2.price)[0]); */
    const groupValues = Object.values(groups);
    for (const fuelType of groupValues) {
      const fuelTypeArray = fuelType as Array<BusinessFuel>;
      fuelTypeArray.sort((f1: BusinessFuel, f2: BusinessFuel) => f1.price - f2.price);
      retVal.push(fuelTypeArray[0]);
    }
    // const retVal = Object.values(groups).map(fuelPrices => fuelPrices[0]);
    return retVal;
  }

  /**
   * Returns the text to display for the longest runway length. The logic is based on the following:
   *   - If there are no runways, return an empty string.
   *   - If there is only one runway, just show "Runway Length".
   *   - If there is a mix of paved and unpaved runways, show the longest paved runway.
   *   - If there are no paved runways, just show the longest (unpaved) runway length.
   */
  get longestRunwayText(): string {
    const formatNumber = (value: number) => this._decimalPipe.transform(value);
    const rwyCompareFn = (rwy1: LandingSurface, rwy2: LandingSurface) => rwy2.length - rwy1.length;
    const runways = this.airport.landingSurfaces.filter(rwy => rwy.type === LandingSurfaceType.Runway);
    if (runways.length < 1) {
      return '';
    }
    if (runways.length === 1) {
      return `Runway length: ${formatNumber(runways[0].length)} ft\n` + `(` + formatNumber(runways[0].lengthMeters) + ` m)`;
    }
    const pavedRunways = runways.filter(rwy => rwy.paved);
    if (pavedRunways.length) {
      const longestPavedRwy = pavedRunways.sort(rwyCompareFn)[0];
      return `Longest paved runway: ${formatNumber(longestPavedRwy.length)} ft\n`
        + `(` + formatNumber(longestPavedRwy.lengthMeters) + ` m)`;
    }
    const longestRwy = runways.sort(rwyCompareFn)[0];
    return `Longest runway: ${longestRwy.length} ft\n` + `(` + formatNumber(longestRwy.lengthMeters) + ` m)`;
  }

  /**
   * Query parameters for the Nearby Airports link
   */
  get nearbyParams(): { [k: string]: any } {
    if (!this.airport) {
      return null;
    }
    const query = new SearchQuery();
    query.location = this.airport.location;
    query.radius = NEARBY_RADIUS;
    return query.encode();
  }

  get favoriteValid(): boolean {
    return this._userInfo && this._userInfo.favoriteAirports.length > 0;
  }

  get validationReason(): string {
    if (!this._userInfo) {
      return 'Please log in to use this feature.';
    }
    if (this._userInfo.favoriteAirports.length < 1) {
      return 'Add airports to your favorites list to use this feature.';
    }
    return null;
  }

  get notSubscribedTooltip(): string {
    if (!this.isIsland || this.isSubscribedToIslandGuide) {
      return '';
    }
    return 'Island Guide subscribers only';
  }

  get transientRamp(): string {
    if (this.airport.afmOther !== null) {
      if (this.airport.afmOther.gatParking === null) {
        return 'Unknown';
      } else if (this.airport.afmOther.gatParking === false) {
        return 'No';
      } else {
        return 'Yes, ';
      }
    } else {
      return 'Unknown';
    }
  }

  get timeZoneText(): string {
    if (!this.airport || !this.airport.timeZone) {
      return null;
    }

    const timeZone = moment().tz(this.airport.timeZone);
    const UTCOffset = '(UTC' + timeZone.format('Z') + ')';

    return timeZone.format('z') + ' ' + UTCOffset;
  }

  getWebcamCount(): number {
    let webcamCount = this.airport.webcams.length;
    this.airport.businesses.forEach( (business) => {
      business.webcams.forEach( (businessWebcam) => {
        webcamCount++;
      });
    });
    return webcamCount;
  }

  get sunrise(): string {
    const today = new Date();
    const times = SunCalc.getTimes(today, this.airport.location.lat, this.airport.location.lon);
    if (this.airport.timeZone != null) {
      return moment(times.sunrise).tz(this.airport.timeZone).format('h:mm a');
    } else {
      return moment(times.sunrise).format('h:mm a');
    }
  }

  get sunset(): string {
    const today = new Date();
    const times = SunCalc.getTimes(today, this.airport.location.lat, this.airport.location.lon);
    if (this.airport.timeZone != null) {
      return moment(times.sunset).tz(this.airport.timeZone).format('h:mm a');
    } else {
      return moment(times.sunset).format('h:mm a');
    }
  }

  get pmCivilTwilightEnd(): string {
    const today = new Date();
    const times = SunCalc.getTimes(today, this.airport.location.lat, this.airport.location.lon);
    if (this.airport.timeZone != null) {
      return moment(times.dusk).tz(this.airport.timeZone).format('h:mm a');
    } else {
      return moment(times.dusk).format('h:mm a');
    }
  }

  get amCivilTwilightStart(): string {
    const today = new Date();
    const tomorrow = today.setDate(today.getDate() + 1);
    const times = SunCalc.getTimes(tomorrow, this.airport.location.lat, this.airport.location.lon);
    if (this.airport.timeZone != null) {
      return moment(times.dawn).tz(this.airport.timeZone).format('h:mm a');
    } else {
      return moment(times.dawn).format('h:mm a');
    }
  }

  navigateToWx() {
    let tempUrl = this._router.url;
    if (this._router.url.indexOf('?') > 0) {
      tempUrl = this._router.url.substr(0, tempUrl.indexOf('?'));
    }
    if (this._router.url.indexOf('#') > 0) {
      tempUrl = this._router.url.substr(0, tempUrl.indexOf('#'));
    }
    this._router.navigate([tempUrl], {fragment: 'wx'});
  }

  async reloadAirport(identifier: string) {
    this.airport = await this._airportService.getAirport(identifier, true);
  }

  openEditGATransientDialog() {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = {
      airID: this.airport.airportId,
      isNew: false,
    };
    dialogConfig.data.afmOther = this.airport.afmOther;
    dialogConfig.data['airID'] = this.airport.airportId;
    dialogConfig.width = '60%';
    const dialogRef = this._editGATransientDialog.open(EditGaTransientComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this.reloadAirport(this.airport.identifier);
        this._snackBar.open('GA Transient Parking', 'Updated', {
          duration: 2000
        });
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  get whyHereText(): string {
    if (!this.airport || this.airport.airportComments.length < 1) {
      return null;
    }
    if (this.isIsland != null) {
      const comment = this.airport.airportComments.filter(c => c.commentRef === 'WhyHere');

      if (comment.length > 0) {
        const whyHere = comment[0];

        if (whyHere != null) {
          return whyHere.text;
        }
      } else {
        return '';
      }
      return this.whyHereText;
    }
    return '';
  }

  navigateToTFRs() {
    let tempUrl = this._router.url;
    if (this._router.url.indexOf('?') > 0) {
      tempUrl = this._router.url.substr(0, tempUrl.indexOf('?'));
    }
    if (this._router.url.indexOf('#') > 0) {
      tempUrl = this._router.url.substr(0, tempUrl.indexOf('#'));
    }
    this._router.navigate([tempUrl], {fragment: 'notams'});
  }
}
